import React, { useEffect, useState, useRef } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { Global, css } from '@emotion/core';
import styled from '@emotion/styled';
import ResizeObserver from 'react-resize-observer';
import { BorderRadius, BoxShadow, TransitionGlobal } from '../../../shared/GlobalStyles';
import { setIframelyError, addAsset } from '../ducks/calendarActions';

const getModalStyle = (width, borderRadius, minWidth, maxWidth) => ({
  overlay: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    backgroundColor: 'rgba(68, 68, 68, 0.5)',
    alignItems: 'center',
    position: 'fixed',
    zIndex: '100',
    top: '0',
    overflowY: 'auto',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: width || '50vw',
    minWidth: minWidth || '530px',
    maxWidth: maxWidth || '1000px',
    backgroundColor: 'white',
    borderRadius: borderRadius || `${BorderRadius}`,
    boxShadow: `${BoxShadow}`,
  },
});

export const ModalContentWrapper = styled('div')`
  display: flex;
  padding: ${(props) => props.padding};
  flex-grow: 1;
  background-color: ${(props) => props.theme.backgroundColorL2};
`;

const Modal = (props) => {
  const dispatch = useDispatch();
  const iframelyError = useSelector((state) => state.calendar.data.iframelyError);
  const assetToAdd = useSelector((state) => state.calendar.data.addAsset);
  const [modalDimensions, setModalDimensions] = useState({});
  const [containerHeight, setContainerHeight] = useState(null);
  const modalContentRef = useRef(null);

  const {
    children, isOpen, width, padding, borderRadius, minWidth, maxWidth,
  } = props;

  const modalClose = () => {
    const { onRequestClose } = props;
    onRequestClose();
    if (iframelyError) {
      dispatch(setIframelyError(null));
    }
    if (assetToAdd) {
      dispatch((addAsset(null)));
    }
  };

  /** to make the modal scrollable when the window height is small, we save the
  modal dimensions on isOpen, and set the height dynamically in a css media query
  where we make the modal absolute position, and overflow auto. */

  const updateModalDimensions = () => {
    if (modalContentRef?.current) {
      setModalDimensions(modalContentRef.current.clientHeight);
    }
  };

  useEffect(() => {
    /** Update dimensions when modal is open and on resize with height changes */
    if (isOpen) {
      setTimeout(() => {
        updateModalDimensions();
      }, 800);
    }

    updateModalDimensions();
  }, [isOpen, containerHeight]);

  return (
    <>
      <Global
        styles={css`
          .ReactModal__Content {
            opacity: 0;
            transform: translateY(50%);
            &:focus {
            outline: none !important;
            }
          }

          .ReactModal__Overlay {
            background-color: rgba(216, 216, 216, 0.5);
            opacity: 0;
            z-index: 1100!important;
            justify-content: center;
          }

          .ReactModal__Overlay--after-open {
            opacity: 1;
            transition: ${TransitionGlobal};
          }

          .ReactModal__Overlay--before-close {
            opacity: 0;
          }

          .ReactModal__Content--after-open {
            opacity: 1;
            transform: translateY(0);
            transition: ${TransitionGlobal};
          }

          .ReactModal__Content--before-close {
            opacity: 0 !important;
            transform: translateY(50%) !important;
            transition: ${TransitionGlobal} !important;
          }
          @media only screen and (min-device-width : 768px) and (max-device-width : 1024px) {
            .ReactModal__Content {
              width: 75vw!important;
            } 
          }

          @media screen and (max-height: ${modalDimensions}px) {
            .ReactModal__Content {
              position: absolute!important;
              top: 15px!important;
            }
          }

          @media only screen and (max-device-width: 767px) {
            .ReactModal__Content {
              min-width: 95%!important;
            }
            /* The overlay is the scroll container for the modal. Its inline
               height of 100vh extends behind the mobile browser chrome (URL
               bar), so the last ~60-100px of a scrolled modal - typically the
               bottom buttons - are unreachable. dvh tracks the visible
               viewport; browsers without dvh keep the inline 100vh. */
            .ReactModal__Overlay {
              height: 100dvh!important;
            }
          }
        `}
      />
      <ReactModal
        closeTimeoutMS={250}
        isOpen={isOpen}
        onRequestClose={modalClose}
        style={getModalStyle(width, borderRadius, minWidth, maxWidth)}
        className='global-modal'
        overlayClassName='overlay'
      >
        <ModalContentWrapper
          ref={modalContentRef}
          padding={padding}
        >
          <ResizeObserver
            onResize={(rect) => {
              setContainerHeight(rect?.height);
            }}
          />
          {children}
        </ModalContentWrapper>
      </ReactModal>
    </>
  );
};

if (process.env.STORYBOOK_RUNNING === undefined) {
  ReactModal.setAppElement('#app');
}

Modal.propTypes = {
  children: PropTypes.node,
  onRequestClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  width: PropTypes.string,
  minWidth: PropTypes.string,
  maxWidth: PropTypes.string,
  padding: PropTypes.string,
  borderRadius: PropTypes.string,
};

Modal.defaultProps = {
  children: '',
  width: '50vw',
  minWidth: '',
  maxWidth: '',
  padding: '40px',
  borderRadius: BorderRadius || '5px',
};

export default Modal;
